import * as React from 'react';
import { HocOptions } from '../../../../common/generic-hoc.types';
import { StylesProps } from '../../../../../theme/jss-types';
import { RouteChildrenProps } from 'react-router';
import { bindActionCreators } from 'redux';
import { ReduxStore } from '../../../../../reducers/redux.types';
import { Master } from '../../../../../types/master-data-types';
import { Buckets, Pagination } from '../../../../../library/Types';
import {
    applyFilters,
    setFilters,
    setPagination,
} from '../../../../../actions/generic-action';
import { loadConsignments, getMoreFilters } from '../../../../../actions/consignment-actions';
import GenericHoc from '../../../../common/generic-hoc';
import { gridView1Styles } from './grid-view-1.styles';
import FilterBar from './filter-bar';
import GridView1Card from './grid-view-1-card';
import moment from 'moment';
import {
    Spin, Row, Col, message,
} from 'antd';
import {
    downloadConsignments,
} from '../../../../../network/consignments.api';
import { getFilters, buildDeliverySlotTimeFilter } from '../../../consignments.helper';
import { getDownloadsRoute } from '../../../../../routing/routing-helper';
import { useTranslation } from 'react-i18next';

interface GridView1ListViewProps
    extends StylesProps<ReturnType<typeof gridView1Styles>>, RouteChildrenProps {
    master: Master;
    loadconsignments: () => void;
    setfilters: (bucketId: Buckets.CONSIGNMENTS, filters: any) => void;
    applyfilters: (bucketId: Buckets.CONSIGNMENTS) => void;
    setpagination: (bucketId: Buckets.CONSIGNMENTS, newPagination: Partial<Pagination>) => void;
    filters: Record<string, any>;
    loading: boolean;
    consignments: any;
    pagination: Pagination;
    goToDetailsPage: (row: any) => void;
    viewMode: string | null | undefined;
    onViewModeChange: (mode: string | null) => void;
    viewIdFromConfig: string | null | undefined;
    isMoreFilterAdded: () => boolean;
    handleResetFilters: () => void;
}

const GridView1ListViewComponent: React.FC<GridView1ListViewProps> = (props) => {
    const {
        master,
        classes,
        filters,
        setfilters,
        applyfilters,
        loadconsignments,
        consignments,
        loading,
        pagination,
        setpagination,
        goToDetailsPage,
        viewMode,
        onViewModeChange,
        viewIdFromConfig,
        isMoreFilterAdded,
        handleResetFilters,
        history,
    } = props;

    const { config } = master;
    const { t } = useTranslation();
    const [selectedSearchType, setSelectedSearchType] = React.useState<string>(
        filters?.customerReferenceNumberList ? 'customerReferenceNumberList' : 'referenceNumberList',
    );
    const [inputFilterVal, setInputFilterVal] = React.useState<string>(
        filters?.customerReferenceNumberList ?? filters?.referenceNumberList ?? '',
    );
    const [loadingMore, setLoadingMore] = React.useState<boolean>(false);
    const [allConsignments, setAllConsignments] = React.useState<any[]>([]);
    const [hasMore, setHasMore] = React.useState<boolean>(true);
    const [downloading, setDownloading] = React.useState<boolean>(false);

    // Ref for infinite scroll
    const loadMoreRef = React.useRef<HTMLDivElement>(null);

    // Reset to page 1 when switching to grid view if page is not 1
    React.useEffect(() => {
        if (pagination.currentPageNumber !== 1) {
            const newPagination = {
                ...pagination,
                currentPageNumber: 1,
                nextOrPrev: 'first' as const,
            };
            setpagination(Buckets.CONSIGNMENTS, newPagination);
            loadconsignments();
        }
    }, []);

    // Update all consignments when new data arrives
    React.useEffect(() => {
        if (consignments && consignments.length > 0) {
            if (pagination.currentPageNumber === 1) {
                // First page - replace all data
                setAllConsignments(consignments);
            } else {
                // Subsequent pages - append data
                setAllConsignments((prev) => {
                    // Avoid duplicates by filtering based on reference_number
                    const existingIds = new Set(prev.map((c: any) => c.reference_number));
                    const newItems = consignments.filter((c: any) => !existingIds.has(c.reference_number));
                    return [...prev, ...newItems];
                });
            }
            // Check if there are more pages using isNextPresent
            setHasMore(pagination.isNextPresent);
        } else if (pagination.currentPageNumber === 1) {
            // Empty result on first page
            setAllConsignments([]);
            setHasMore(false);
        }
        setLoadingMore(false);
    }, [consignments, pagination.currentPageNumber, pagination.isNextPresent]);

    // Handle load more
    const handleLoadMore = () => {
        if (!hasMore || loading || loadingMore) return;

        setLoadingMore(true);
        const newPagination = {
            ...pagination,
            nextOrPrev: 'next' as const,
            currentPageNumber: pagination.currentPageNumber + 1,
        };
        setpagination(Buckets.CONSIGNMENTS, newPagination);
        loadconsignments();
    };

    // Intersection Observer for infinite scroll
    React.useEffect(() => {
        const handleIntersection = (entries: IntersectionObserverEntry[]) => {
            const firstEntry = entries[0];
            if (firstEntry.isIntersecting && hasMore && !loading && !loadingMore) {
                handleLoadMore();
            }
        };

        const observer = new IntersectionObserver(handleIntersection, { threshold: 0.1 });

        const currentRef = loadMoreRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [hasMore, loading, loadingMore, pagination.currentPageNumber]);

    // Handle date change with 30-day validation
    const handleDateChange = (value: any) => {
        if (!(value && value[0] && value[1])) return;
        const newFilters = {
            ...filters,
        };
        if (value[1].isSame(filters.endDate)) {
            if (moment(filters.endDate).diff(value[0], 'days') > 30) {
                const endDate = moment(value[0]).add(30, 'd');
                newFilters.endDate = endDate.unix() * 1000;
                newFilters.startDate = moment(value[0]).unix() * 1000;
            } else {
                newFilters.endDate = moment(value[1]).unix() * 1000;
                newFilters.startDate = moment(value[0]).unix() * 1000;
            }
        } else if (moment(value[1]).diff(value[0], 'days') > 30) {
            const startDate = moment(value[1]).subtract(30, 'd');
            newFilters.startDate = startDate.unix() * 1000;
            newFilters.endDate = moment(value[1]).unix() * 1000;
        } else {
            newFilters.endDate = moment(value[1]).unix() * 1000;
            newFilters.startDate = moment(value[0]).unix() * 1000;
        }
        setfilters(Buckets.CONSIGNMENTS, newFilters);
        applyfilters(Buckets.CONSIGNMENTS);
    };

    // Handle filter changes
    const onChangeFilter = (filter: string, value: any) => {
        const newFilters = {
            ...filters,
            [filter]: value || undefined,
        };
        setfilters(Buckets.CONSIGNMENTS, newFilters);
        applyfilters(Buckets.CONSIGNMENTS);
    };

    // Handle refresh
    const handleRefresh = () => {
        loadconsignments();
    };

    // Handle download
    const handleDownload = async () => {
        const dateFormat = 'YYYY-MM-DD';
        setDownloading(true);
        const moreFilters = getMoreFilters(filters);

        const filtersInfo = getFilters(
            filters.bucketSelected || 'customer_portal_all',
            config,
        );
        filtersInfo.forEach((filter: any) => {
            if (moreFilters[filter.id] && filter.mode === 'multiple' && typeof moreFilters[filter.id] === 'string') {
                moreFilters[filter.id] = moreFilters[filter.id].split(',').map((value: string) => value.trim());
            }
        });

        const deliverySlotValue = moreFilters.delivery_time_slot || moreFilters.deliveryTimeslot;
        const filteredMoreFilters = { ...moreFilters };
        delete filteredMoreFilters.delivery_time_slot;
        delete filteredMoreFilters.deliveryTimeslot;

        const baseTimeFilters = [
            {
                columnName: filters.dateType || filters.sortBy,
                fromDateString: moment(filters.startDate).format(dateFormat),
                toDateString: moment(filters.endDate).format(dateFormat),
            },
        ];
        const deliverySlotTimeFilter = buildDeliverySlotTimeFilter(deliverySlotValue);
        const finalizedTimeFilters = deliverySlotTimeFilter
            ? [...baseTimeFilters, deliverySlotTimeFilter]
            : baseTimeFilters;

        const response = await downloadConsignments({
            customerIdList: [],
            bucket: filters.bucketSelected,
            timeFilters: finalizedTimeFilters,
            descendingOrder: filters.descendingOrder,
            referenceNumberList: [],
            ...filteredMoreFilters,
        });
        if (response.isSuccess) {
            message.success('Requested Successfully');
            history.push(getDownloadsRoute());
        } else {
            message.error(response.errorMessage);
        }
        setDownloading(false);
    };

    // Handle search type change
    const handleSearchTypeChange = (value: string) => {
        setSelectedSearchType(value);
    };

    // Handle search value change
    const handleSearchValueChange = (value: string) => {
        setInputFilterVal(value);
    };

    // Handle search input submit (clears both filters and applies the selected one)
    const handleSearchInputSubmit = (searchType: string, searchValue: string) => {
        const newFilters = {
            ...filters,
            referenceNumberList: undefined,
            customerReferenceNumberList: undefined,
            [searchType]: searchValue && searchValue.trim() ? searchValue.trim() : undefined,
        };
        setfilters(Buckets.CONSIGNMENTS, newFilters);
        applyfilters(Buckets.CONSIGNMENTS);
    };

    // Render content - always renders grid cards
    const renderContent = () => {
        // Initial loading state
        if (loading && pagination.currentPageNumber === 1) {
            return (
                <div className={classes.centerContainer}>
                    <Spin size="large" />
                </div>
            );
        }

        // Empty state
        if (allConsignments.length === 0 && !loading) {
            return (
                <div className={classes.centerContainer}>
                    <div className={classes.emptyStateText}>{t('no_data_found')}</div>
                </div>
            );
        }

        // Grid view with consignment cards
        return (
            <div className={classes.cardsContainer}>
                <Row gutter={[24, 24]}>
                    {allConsignments.map((consignment: any) => (
                        <Col key={consignment.reference_number} span={6} xs={24} sm={12} md={12} lg={8} xl={6}>
                            <GridView1Card
                                consignment={consignment}
                                onClick={() => goToDetailsPage(consignment)}
                                classes={classes}
                            />
                        </Col>
                    ))}
                </Row>

                {/* Load More Trigger */}
                {hasMore && (
                    <div ref={loadMoreRef} className={classes.loadMoreContainer}>
                        {loading && <Spin />}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={classes.mainDiv}>
            <FilterBar
                classes={classes}
                filters={filters}
                onDateChange={handleDateChange}
                onChangeFilter={onChangeFilter}
                onRefresh={handleRefresh}
                onDownload={handleDownload}
                downloading={downloading}
                onSearchTypeChange={handleSearchTypeChange}
                onSearchValueChange={handleSearchValueChange}
                onSearchInputSubmit={handleSearchInputSubmit}
                selectedSearchType={selectedSearchType}
                inputFilterVal={inputFilterVal}
                filterMetadata={config?.filter_metadata || []}
                viewMode={viewMode}
                onViewModeChange={onViewModeChange}
                viewIdFromConfig={viewIdFromConfig}
                master={master}
                isMoreFilterAdded={isMoreFilterAdded}
                handleResetFilters={handleResetFilters}
            />
            {renderContent()}
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { generic, master } = state;
    const { CONSIGNMENTS } = generic;
    const {
        loading,
        data,
        pagination,
        appliedFilters,
    } = CONSIGNMENTS;

    return {
        master,
        filters: appliedFilters,
        loading,
        consignments: data,
        pagination,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    const actions = {
        loadconsignments: loadConsignments,
        setfilters: setFilters,
        applyfilters: applyFilters,
        setpagination: setPagination,
    };
    return bindActionCreators(actions, dispatch);
};

const hocConfig: HocOptions = {
    connectRouter: true,
    connectJss: {
        useJss: true,
        styleSheet: gridView1Styles,
    },
    connectRedux: {
        useRedux: true,
        mapStateToProps,
        mapDispatchToProps,
    },
};

const GridView1ListView = GenericHoc(hocConfig)(GridView1ListViewComponent);

export default GridView1ListView;
